/**
 * ConfirmDialog Component
 *
 * A generic confirmation dialog for dangerous or important actions.
 * Supports three confirmation styles: danger, warning, success.
 *
 * @module features/tenants/components/modals/ConfirmDialog
 */

import type { ConfirmDialogState } from '../../types';

interface ConfirmDialogProps {
  /** Dialog state or null when closed */
  dialog: ConfirmDialogState | null;
  /** Callback to close the dialog */
  onClose: () => void;
}

/**
 * Renders a styled confirmation dialog.
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({ dialog, onClose }) => {
  if (!dialog) {
    return null;
  }

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        background: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--glass-bg)',
          backdropFilter: 'var(--glass-blur-lg)',
          WebkitBackdropFilter: 'var(--glass-blur-lg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-2xl)',
          boxShadow: 'var(--glass-shadow-lg)',
          padding: '0',
          width: '100%',
          maxWidth: '420px',
          animation: 'modal-appear 0.2s ease-out',
        }}
      >
        <div style={{
          padding: '24px 24px 16px',
          borderBottom: '1px solid var(--border-secondary)',
        }}>
          <h3 style={{
            margin: 0,
            fontSize: 'var(--text-xl)',
            fontWeight: 600,
            color: 'var(--text-primary)',
          }}>
            {dialog.title}
          </h3>
        </div>
        <div style={{
          padding: '20px 24px',
        }}>
          <p style={{
            margin: 0,
            fontSize: 'var(--text-base)',
            color: 'var(--text-secondary)',
            lineHeight: 1.6,
          }}>
            {dialog.message}
          </p>
        </div>
        <div style={{
          padding: '16px 24px 24px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
        }}>
          <button
            className="btn btn-secondary"
            onClick={onClose}
            style={{ minWidth: '80px' }}
          >
            Cancel
          </button>
          <button
            className={`btn ${
              dialog.confirmStyle === 'danger' ? 'btn-error' :
              dialog.confirmStyle === 'warning' ? 'btn-warning' :
              'btn-success'
            }`}
            onClick={dialog.onConfirm}
            style={{ minWidth: '100px' }}
          >
            {dialog.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
